'use client'

import { X24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, Image, Label, Navigation, Text } from '@vinted/web-ui'
import { MouseEvent, useEffect } from 'react'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { useAsset } from '@marketplace-web/shared/assets'
import {
  getShippingInstructions,
  transformShippingInstructionsResponse,
  shippingInstructionsTimeOnTaskEvent,
  clickEvent,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

import GenericErrorModal from '../GenericErrorModal'
import useTimeOnTaskTracking from '../../hooks/useTimeOnTaskTracking'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const ViewDeliveryInstructionsModal = ({ isOpen, onClose }: Props) => {
  const translate = useTranslate('conversation.view_delivery_instructions.modal')
  const { track } = useTracking()
  const asset = useAsset('/assets/shipping')
  const { conversation } = useConversationContext()

  const transactionId = conversation?.transaction?.id
  const transactionStatus = conversation?.transaction?.status
  const shipmentId = conversation?.transaction?.shipment?.id
  const shipmentStatus = conversation?.transaction?.shipment?.status

  const CARRIER_ICON_PLACEHOLDER = 'carrier-placeholder.svg'

  const {
    fetch: fetchShippingInstructions,
    transformedData: shippingInstructions,
    isLoading,
    error,
  } = useFetch(getShippingInstructions, response => transformShippingInstructionsResponse(response))

  const trackTimeOnTask = useTimeOnTaskTracking({
    screen: 'delivery_instructions',
    withEventListeners: true,
    shipmentId,
    trackingFunction: shippingInstructionsTimeOnTaskEvent,
  })

  useEffect(() => {
    if (!transactionId || !isOpen) return

    fetchShippingInstructions(transactionId)
  }, [transactionId, isOpen, fetchShippingInstructions])

  useEffect(() => {
    if (!isOpen) return undefined

    return () => trackTimeOnTask()
  }, [isOpen, trackTimeOnTask])

  const handleLinkClick = (event: MouseEvent) => {
    if (!(event.target instanceof HTMLAnchorElement)) return

    const targetDetails = {
      transaction_id: transactionId,
      url: event.target.href,
      transaction_status: transactionStatus,
      shipment_id: shipmentId,
      shipment_status: shipmentStatus,
    }

    track(
      clickEvent({
        screen: 'delivery_instructions',
        target: 'hyperlink',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const renderCarrier = () => {
    return (
      <>
        <Label type={Label.Type.Stacked} text={translate('label')} />
        <Cell
          prefix={
            <Image
              src={shippingInstructions?.carrier.iconUrl}
              size={Image.Size.Large}
              styling={Image.Styling.Rounded}
              fallbackSrc={asset(CARRIER_ICON_PLACEHOLDER)}
            />
          }
          title={
            <Text
              as="h2"
              text={shippingInstructions?.carrier.name}
              type={Text.Type.Title}
              theme="amplified"
            />
          }
          body={<Text as="span" text={translate('body')} />}
          testId="view-delivery-instructions-modal-carrier"
        />
      </>
    )
  }

  const renderShippingInstructions = () => {
    return (
      <>
        <Label type={Label.Type.Stacked} text={translate('instructions_field_label')} />
        <Cell onClick={handleLinkClick} testId="view-delivery-instructions-modal-instructions">
          <Text as="span" html text={shippingInstructions?.instructions} />
        </Cell>
      </>
    )
  }

  const handleClose = () => onClose()

  const renderSubmit = () => (
    <Cell>
      <Button
        onClick={handleClose}
        styling={Button.Styling.Filled}
        text={translate('actions.got_it')}
        testId="view-delivery-instructions-modal-submit"
      />
    </Cell>
  )

  const renderContent = () => {
    if (isLoading)
      return (
        <Dialog show closeOnOverlay>
          <ContentLoader testId="view-delivery-instructions-modal-loader" />
        </Dialog>
      )

    if (error) return <GenericErrorModal isShown={isOpen} onClose={onClose} />

    return (
      <Dialog show={isOpen} closeOnOverlay testId="view-delivery-instructions-modal">
        <Navigation
          body={translate('title')}
          right={
            <Button
              styling={Button.Styling.Flat}
              onClick={handleClose}
              iconName={X24}
              inline
              testId="view-delivery-instructions-modal-close"
            />
          }
        />
        <Divider />
        <div className="modal-contained-content">
          <SeparatedList separator={<Divider />}>
            {renderCarrier()}
            {renderShippingInstructions()}
            {renderSubmit()}
          </SeparatedList>
        </div>
      </Dialog>
    )
  }

  return renderContent()
}

export default ViewDeliveryInstructionsModal
